import React, {useEffect, useRef, useState, useCallback} from 'react';
import {useRouter} from 'next/router';
import {getLocaleContent} from '@helper/locale-content.js';
import {useNewsStore} from '@stores/useNewsStore';
import {useMasterDataStore} from '@stores/useMasterDataStore';
import NewsService from '@services/news.service';
import FilterInput from '@components/common/FilterInput';
import LinkNewsDetail from '@components/common/Link/LinkNewsDetail';
import LoadingNavigation from '@components/common/Loading/LoadingNavigation';

export default function NewsList() {
    const router = useRouter();
    const {locale} = router;

    /**
     * Refs để quản lý trạng thái và tránh re-render không cần thiết
     *
     * - searchTimeoutRef: Lưu timeout ID của debounce search
     * - didInitRef: Chặn double-fetch khi component mount hoặc đổi locale
     * - allowLoadMoreRef: Chỉ cho phép load more sau khi trang đầu tiên load xong
     * - urlSyncedRef: Đánh dấu đã sync URL query params vào filter chưa (tránh loop vô hạn)
     */
    const searchTimeoutRef = useRef(null);
    const didInitRef = useRef(false);
    const allowLoadMoreRef = useRef(false);
    const urlSyncedRef = useRef(false);

    // Lấy dữ liệu từ news store
    const news = useNewsStore(s => s.news);
    const pageIndex = useNewsStore(s => s.pageIndex);
    const hasMore = useNewsStore(s => s.hasMore);
    const totalItems = useNewsStore(s => s.totalItems);
    const isLoading = useNewsStore(s => s.isLoading);
    const filters = useNewsStore(s => s.filters);

    // Lấy actions từ news store
    const setFiltersStore = useNewsStore(s => s.setFilters);
    const setNewsStore = useNewsStore(s => s.setNews);
    const appendNewsStore = useNewsStore(s => s.appendNews);
    const resetFiltersStore = useNewsStore(s => s.resetFilters);
    const setLoadingStore = useNewsStore(s => s.setLoading);
    const hasActiveFilters = useNewsStore(s => s.hasActiveFilters);

    // Lấy categories từ master data store
    const categories = useMasterDataStore(s => s.newsCategories);
    const fetchNewsCategories = useMasterDataStore(s => s.fetchNewsCategories);

    // Lấy danh sách tin tức (cho load more)
    const fetchNews = useCallback(
        (page = 1, isLoadMore = false) => {
            setLoadingStore(true);
            const newsService = new NewsService();
            const currentFilters = useNewsStore.getState().filters || {};

            return newsService
                .getNewsList({
                    locale,
                    pageSize: 10,
                    pageIndex: page,
                    keyword: currentFilters.keyword,
                    news_category_id: currentFilters.categoryId,
                    is_news: 1, // chỉ lấy tin tức
                })
                .then(res => {
                    const items = res?.data || [];
                    const total = parseInt(res?.total) || 0;

                    if (isLoadMore) {
                        appendNewsStore(items, total, page, 10);
                    } else {
                        setNewsStore(items, total, page, 10);
                        // Sau khi trang đầu hoàn tất mới cho phép load-more
                        allowLoadMoreRef.current = true;
                    }
                })
                .catch(err => {
                    console.error('Lỗi khi tải tin tức:', err);
                })
                .finally(() => {
                    setLoadingStore(false);
                });
        },
        [setLoadingStore, setNewsStore, appendNewsStore, locale],
    );

    // Lấy danh sách categories từ useMasterDataStore
    const fetchCategories = useCallback(() => {
        fetchNewsCategories(locale);
    }, [fetchNewsCategories, locale]);

    // Function chung để load news với filters
    const loadNewsWithFilters = useCallback(
        (categoryId = '', keyword = '') => {
            setLoadingStore(true);
            const newsService = new NewsService();

            return newsService
                .getNewsList({
                    locale,
                    pageSize: 10,
                    pageIndex: 1,
                    keyword,
                    is_news: 1,
                    news_category_id: categoryId,
                })
                .then(res => {
                    const items = res?.data || [];
                    const total = parseInt(res?.total) || 0;
                    setNewsStore(items, total, 1, 10);
                    // Reset trạng thái load-more sau khi filter/search
                    allowLoadMoreRef.current = true;
                })
                .catch(err => {
                    console.error('Lỗi khi tải tin tức:', err);
                })
                .finally(() => {
                    setLoadingStore(false);
                });
        },
        [setLoadingStore, setNewsStore, locale],
    );

    // Xử lý tìm kiếm với debounce
    const handleSearchChange = useCallback(
        value => {
            setFiltersStore({keyword: value});

            // Clear timeout cũ nếu có
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

            // Chỉ tìm kiếm khi có ít nhất 4 ký tự hoặc xóa hết
            if (value.length >= 4 || value.length === 0) {
                // khóa load-more trong lúc tải lại trang 1
                allowLoadMoreRef.current = false;
                searchTimeoutRef.current = setTimeout(() => {
                    const currentFilters = useNewsStore.getState().filters || {};
                    loadNewsWithFilters(currentFilters.categoryId, value);
                }, 500);
            }
        },
        [setFiltersStore, loadNewsWithFilters],
    );

    // Xử lý tìm kiếm khi nhấn Enter
    const handleEnterSearch = useCallback(
        value => {
            if (value.length) {
                setFiltersStore({keyword: value});
                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                allowLoadMoreRef.current = false; // khóa trong lúc tải lại trang 1
                const currentFilters = useNewsStore.getState().filters || {};
                loadNewsWithFilters(currentFilters.categoryId, value);
            }
        },
        [setFiltersStore, loadNewsWithFilters],
    );

    /**
     * Xử lý thay đổi category - Sử dụng URL làm single source of truth
     *
     * Khi click tab category:
     * 1. Thay đổi URL thay vì set filter trực tiếp
     * 2. useEffect sẽ tự động detect URL change và sync vào filter
     * 3. Tự động fetch data theo category mới
     *
     * Ưu điểm:
     * - URL luôn sync với trạng thái hiện tại
     * - Browser back/forward hoạt động đúng
     * - Có thể share link với category cụ thể
     * - Dễ debug và maintain
     */
    const handleCategoryChange = useCallback(
        value => {
            if (value) {
                // Có category: thêm query param vào URL
                // VD: /tin-tuc?news_category_id=21
                router.push(
                    {
                        pathname: '/tin-tuc',
                        query: {news_category_id: value},
                    },
                    undefined,
                    {shallow: true},
                ); // shallow: true để không reload trang
            } else {
                // "Tất cả": xóa query param khỏi URL
                // VD: /tin-tuc
                router.push('/tin-tuc', undefined, {shallow: true});
            }
        },
        [router],
    );

    // Load categories khi component mount
    useEffect(() => {
        if (!locale) return;
        fetchCategories();
    }, [locale, fetchCategories]);

    /**
     * Effect 1: Reset flag khi URL query param thay đổi
     *
     * Trigger khi:
     * - router.query.news_category_id thay đổi (click tab, navigate từ footer, browser back/forward)
     * - categories load xong lần đầu
     *
     * Action:
     * - Set urlSyncedRef = false để cho phép Effect 2 chạy và sync lại data
     */
    useEffect(() => {
        if (!router.isReady || !categories.length) return;

        urlSyncedRef.current = false;
    }, [router.isReady, router.query?.news_category_id, categories.length]);

    /**
     * Effect 2: Đồng bộ URL query param vào filter và fetch data
     *
     * Flow hoạt động:
     * 1. Đợi router.isReady = true (Next.js đã parse xong query params)
     * 2. Đợi categories load xong
     * 3. Check urlSyncedRef - nếu đã sync rồi thì skip (tránh loop vô hạn)
     * 4. Đọc news_category_id từ URL
     * 5. Set vào filter store
     * 6. Fetch tin tức theo category
     * 7. Set urlSyncedRef = true để đánh dấu đã sync
     *
     * Các trường hợp:
     * - F5 trang với URL có param: sync param vào filter
     * - Navigate từ Footer: sync param mới
     * - Click tab: URL thay đổi → Effect 1 reset flag → Effect 2 sync lại
     * - Browser back/forward: URL thay đổi → tự động sync
     */
    useEffect(() => {
        if (!router.isReady || !categories.length) return;
        if (urlSyncedRef.current) return;

        const categoryIdFromUrl = router.query?.news_category_id || '';

        // Sync URL vào filter và fetch data
        setFiltersStore({categoryId: categoryIdFromUrl});
        allowLoadMoreRef.current = false;
        loadNewsWithFilters(categoryIdFromUrl, '');
        urlSyncedRef.current = true;
    }, [router.isReady, categories.length, router.query?.news_category_id]);

    // Xử lý load more tin tức
    const handleLoadMore = useCallback(() => {
        if (allowLoadMoreRef.current && hasMore && !isLoading) {
            fetchNews((pageIndex || 1) + 1, true);
        }
    }, [hasMore, isLoading, pageIndex, fetchNews]);

    // Infinite scroll effect (chống spam & chỉ chạy khi đã cho phép load-more)
    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!allowLoadMoreRef.current) return; // chưa cho load-more
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const nearBottom =
                    window.innerHeight + document.documentElement.scrollTop >=
                    document.documentElement.offsetHeight - 1000;
                if (nearBottom) handleLoadMore();
                ticking = false;
            });
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleLoadMore]);

    // Dọn dẹp debounce khi unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    return (
        <section className="news-section">
            {/* Header với filters và search */}
            <div className="news-header">
                <h2>{getLocaleContent(locale, 'page.news-page.title')}</h2>
                <div className="line">
                    {/* Category filters */}
                    <div className="news-filters-wrapper">
                        <div className="news-filters">
                            {/* 
                                Tab "Tất cả": Active khi URL không có news_category_id
                                VD: /tin-tuc (không có query param)
                            */}
                            <button
                                className={!router.query?.news_category_id ? 'active' : ''}
                                onClick={() => handleCategoryChange('')}>
                                {getLocaleContent(locale, 'page.news-page.all')}
                            </button>
                            {/* 
                                Các tab category: Active khi news_category_id trong URL khớp với category.news_category_id
                                VD: /tin-tuc?news_category_id=21 → Tab có id=21 sẽ active
                                
                                Chỉ hiển thị category có is_news = 1 (tin tức thật, không phải ưu đãi/gói dịch vụ)
                            */}
                            {categories
                                ?.filter(category => category.is_news === 1)
                                .map(category => (
                                    <button
                                        key={category.news_category_id}
                                        className={
                                            router.query?.news_category_id === category.news_category_id ? 'active' : ''
                                        }
                                        onClick={() => handleCategoryChange(category.news_category_id)}>
                                        {category.news_category_name}
                                    </button>
                                ))}
                        </div>
                    </div>

                    {/* Search input */}
                    <div className="news-search">
                        <FilterInput
                            placeholder={getLocaleContent(locale, 'page.news-page.search-placeholder')}
                            value={filters?.keyword || ''}
                            onChange={e => handleSearchChange(e.target.value)}
                            onEnter={handleEnterSearch}
                        />
                    </div>
                </div>
            </div>

            {/* Danh sách tin tức */}
            <div className="news-grid">
                {news.length === 0 && !isLoading && totalItems === 0 && (
                    <div>{getLocaleContent(locale, 'page.news-page.no-news')}</div>
                )}
                {news.map((newsItem, index) => (
                    <LinkNewsDetail key={newsItem.news_id || index} news={newsItem}>
                        <div className="news-card">
                            <div className="news-image">
                                <img src={newsItem.image_url || '/bw_image/news-image.png'} alt={newsItem.news_title} />
                            </div>
                            <div className="news-body">
                                <div className="news-meta">
                                    <span className="category">{newsItem.news_category_name}</span>
                                    <span className="date">{newsItem.news_date}</span>
                                </div>
                                <h3 className="news-title">{newsItem.news_title}</h3>
                                <p className="news-description">{newsItem.short_description}</p>
                                <div className="news-red-more">
                                    <span className="read-more">
                                        {getLocaleContent(locale, 'page.news-page.view-detail')} →
                                    </span>
                                </div>
                            </div>
                        </div>
                    </LinkNewsDetail>
                ))}
            </div>

            {/* Loading spinner */}
            {isLoading && <LoadingNavigation size="large" color="#006940" style={{marginBottom: '40px'}} />}
        </section>
    );
}
